import { createSelector } from 'reselect';
import { expVal } from '@atlassian/jira-feature-experiments';
import { CHILD_ISSUES_PANEL_TYPE } from '../../common/constants.tsx';
import type { ChildIssuesPanelType } from '../../common/types.tsx';
import { contextSelector } from '../selectors.tsx';
import { getSelectedIssueType, isFetchIssueTypesComplete } from '../ui/selectors.tsx';

export const getBaseUrl = createSelector(contextSelector, (contextState) => contextState.baseUrl);

export const getLocale = createSelector(contextSelector, (contextState) => contextState.locale);

export const getChildIssuesPanelType = createSelector(
	contextSelector,
	(contextState) => contextState.childIssuesPanelType,
);

export const getIsClassicSubtaskPanel = createSelector(
	getChildIssuesPanelType,
	(childIssuesPanelType) =>
		childIssuesPanelType
			? childIssuesPanelType === CHILD_ISSUES_PANEL_TYPE.CLASSIC_SUBTASKS
			: false,
);

export const getIsClassicProjectEpicPanel = createSelector(
	getChildIssuesPanelType,
	(childIssuesPanelType) =>
		childIssuesPanelType
			? childIssuesPanelType === CHILD_ISSUES_PANEL_TYPE.CLASSIC_PROJECT_EPIC_CHILDREN
			: false,
);

export const getIsChildrenIssuesPanel = createSelector(
	getChildIssuesPanelType,
	(childIssuesPanelType) =>
		childIssuesPanelType
			? childIssuesPanelType === CHILD_ISSUES_PANEL_TYPE.CHILDREN_ISSUES_PANEL
			: false,
);

export const getSupportsIssueCreation = createSelector(
	contextSelector,
	(contextState) => contextState.supportsIssueCreation,
);

export const isAnonymousUser = createSelector(
	contextSelector,
	(contextState) => contextState.user === null || contextState.user === '',
);

export const getRequiresFullCreateByIssueType = createSelector(
	contextSelector,
	getSelectedIssueType,
	isFetchIssueTypesComplete,
	isAnonymousUser,
	(context, selectedIssueType, fetchIssueTypesComplete, anonymousUser) => {
		const requiresFullCreateByIssueType =
			selectedIssueType?.id && context.requiresFullCreateByIssueType[selectedIssueType.id];

		return anonymousUser || requiresFullCreateByIssueType || !fetchIssueTypesComplete;
	},
);

export const getIssueTypeWFValidators = createSelector(
	contextSelector,
	isFetchIssueTypesComplete,
	(context, fetchIssueTypesComplete) =>
		fetchIssueTypesComplete ? context.requiresFullCreateByIssueType : undefined,
);

export const getParentKey = createSelector(
	contextSelector,
	(contextState) => contextState.parentKey,
);

export const getOnEditAssociatedIssue = createSelector(
	contextSelector,
	(contextState) => contextState.onEditAssociatedIssue,
);

export const getParentId = createSelector(contextSelector, (contextState) => contextState.parentId);

export const getParentIssueTypeId = createSelector(
	contextSelector,
	(contextState) => contextState.parentIssueTypeId,
);

// TODO: Remove on jira-issue-view-child-issues-panel-merge-subtasks cleanup
export const getFilterSubtasks = createSelector(
	contextSelector,
	(contextState) => contextState.filterSubtasks,
);

export const getCustomFieldIdRank = createSelector(
	contextSelector,
	(contextState) => contextState.customFieldIdRank,
);

export const getEpicLinkFieldKey = createSelector(
	contextSelector,
	(contextState) => contextState.epicLinkFieldKey,
);

export const getProjectId = createSelector(
	contextSelector,
	(contextState) => contextState.projectId,
);

const getMessages = createSelector(contextSelector, (contextState) => contextState.messages || {});

export const getTitle = createSelector(getMessages, (messages) => {
	if (
		// @ts-expect-error - TS2339 - Property 'title' does not exist on type '{}'.
		messages.titleIssueTerminologyRefreshM4 &&
		expVal('jira-itr-work-type-exp-1', 'isEnabled', false)
	) {
		// @ts-expect-error - TS2339 - Property 'title' does not exist on type '{}'.
		return messages.titleIssueTerminologyRefreshM4;
	}
	// @ts-expect-error - TS2339 - Property 'title' does not exist on type '{}'.
	return messages.titleIssueTermRefresh &&
		expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
		? // @ts-expect-error - TS2339 - Property 'title' does not exist on type '{}'.
			messages.titleIssueTermRefresh
		: // @ts-expect-error - TS2339 - Property 'title' does not exist on type '{}'.
			messages.title;
});

// @ts-expect-error - TS2339 - Property 'titleChildIssues' does not exist on type '{}'.
export const getChildTitle = createSelector(getMessages, (messages) => messages.titleChildIssues);

export const getNoValueText = createSelector(
	getMessages,
	// @ts-expect-error - TS2339 - Property 'noValueText' does not exist on type '{}'.
	(messages) => messages.noValueText || null,
);

export const getOnNavigateToNewIssue = createSelector(
	contextSelector,
	(contextState) => contextState.onNavigateToNewIssue,
);

export const getOnChangeCallback = createSelector(
	contextSelector,
	(contextState) => contextState.onChange,
);

export const getIsIssueViewComplete = createSelector(
	contextSelector,
	(contextState) => contextState.isIssueViewComplete,
);

export const getChildIssuesPanelProjectType = createSelector(
	contextSelector,
	(contextState) => contextState.projectType,
);

export const getIsSimplifiedProject = createSelector(
	contextSelector,
	(contextState) => contextState.isSimplifiedProject,
);

export const getChildIssuesPanelApplication = createSelector(
	contextSelector,
	(contextState) => contextState.application,
);

export const getChildIssuesPanelEdition = createSelector(
	contextSelector,
	(contextState) => contextState.edition,
);

// extracted logic in use by selector below so we can use it from hooks as well
export const isWhitelistedPanelForSorting = (panelType: ChildIssuesPanelType | null) =>
	panelType === CHILD_ISSUES_PANEL_TYPE.CHILDREN_ISSUES_PANEL ||
	panelType === CHILD_ISSUES_PANEL_TYPE.CLASSIC_PROJECT_EPIC_CHILDREN;

export const isCompatibleSortingPanel = createSelector(
	getChildIssuesPanelType,
	isWhitelistedPanelForSorting,
);
